import { getDisplayLocale } from "./format-price";

/** Formats an ISO 8601 date string using the configured display locale. */
export function formatDate(dateString: string, options?: Intl.DateTimeFormatOptions): string {
  const locale = getDisplayLocale();
  const defaultOptions: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
  return new Date(dateString).toLocaleDateString(locale, options ?? defaultOptions);
}

/** Formats an ISO 8601 date range using the configured display locale. */
export function formatDateRange(startDate: string, endDate: string): string {
  const locale = getDisplayLocale();
  const options: Intl.DateTimeFormatOptions = { month: "long", day: "numeric", year: "numeric" };
  const start = new Date(startDate).toLocaleDateString(locale, options);
  const end = new Date(endDate).toLocaleDateString(locale, options);
  return `${start} → ${end}`;
}

export type TemporalStatus = "past" | "current" | "future";

/** Classifies a date range against today using ISO-8601 lexicographic order. */
export function getTemporalStatus(startDate: string, endDate: string): TemporalStatus {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (startDate > today) return "future";
  if (endDate < today) return "past";
  return "current";
}

const regionDisplayNames = new Map<string, Intl.DisplayNames>();

/** Returns a localized country/region name for an ISO 3166-1 alpha-2 code. */
export function getCountryName(code: string): string {
  const locale = getDisplayLocale();
  let displayNames = regionDisplayNames.get(locale);
  if (!displayNames) {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
    regionDisplayNames.set(locale, displayNames);
  }
  return displayNames.of(code) ?? code;
}
